import { TEMPO_ID } from 'src/services/deserializers/deserializers.const'
import type {
  FieldControl,
  ProxyRecord,
} from 'src/services/deserializers/deserializers.types'
import { getFormattedErrors } from 'src/services/deserializers/deserializers.utils'
import {
  getFieldMapping,
  getFields,
  getFieldsAndRelationsByOriginKey,
  getRelations,
  getRelationshipMapping,
} from 'src/services/mappings/getMappings'
import type {
  FieldInterface,
  RelationInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import { FIELD_TYPES } from 'src/tpbp-core/models/fields/field-set.const'
import type { Warning } from 'src/tpbp-core/models/warning/warnings.types'
import {
  deepCopy,
  getUUID,
  isEqual,
  random,
  valueIsEmpty,
  zipObject,
} from 'src/tpbp-core/utils/common.utils'
import { StoreDictionary } from 'src/tpbp-json-api-handler/utils/StoreDictionary/StoreDictionary'
import { evalExpr } from 'src/utils/fieldRules/fieldRules'
import { logger } from 'src/utils/logger.utils'
import { setDefaultValues } from 'src/utils/record.utils'
import _ from 'underscore'
import { recordStore } from './recordStore'

const storeDictionaryFields = new StoreDictionary()

function getter(object, key) {
  if (typeof key === 'symbol' || key === 'id') {
    return object[key]
  }

  const firstLetter = key.slice(0, 1)

  if (firstLetter === '$' || firstLetter === '@') {
    return object[key]
  }

  if (firstLetter === '_') {
    const mapping = getRelationshipMapping(object.$type)
    const relation = mapping[key]

    if (relation) {
      const { originKey, isMultiple, byId } = relation

      const relationship = object.$relationships[originKey]
      const data = relationship ? relationship.data : null

      if (isMultiple) {
        if (byId) {
          if (!object.$relationshipsById[key]) {
            object.$relationshipsById[key] = data ? _.indexBy(data, 'id') : {}
          }

          return object.$relationshipsById[key]
        }
        if (!data) {
          return []
        }
        if (!Array.isArray(data)) {
          logger.error(`Expected array for relationship '${key}'`)
          return []
        }
        return data.map((row) => {
          if (recordStore.has(row.type, row.id)) {
            return recordStore.get(row.type, row.id)
          }

          return row
        })
      }

      if (!data) {
        return null
      }
      if (recordStore.has(data.type, data.id)) {
        return recordStore.get(data.type, data.id)
      }

      return data
    }
  }
  const mapping = getFieldMapping(object.$type)

  if (mapping[key]) {
    if (typeof mapping[key] === 'function') {
      // @ts-expect-error when mapping[key] is a function ???
      return mapping[key](object)
    }
    const originKey = mapping[key]

    if (!originKey)
      throw new Error(`Failed to get field mapping for key '${key}'`)
    const field = getField(object.$type, key)
    if (field.isPseudoAttribute && typeof field.expression === 'function') {
      refreshField(object, field)
    }
    if (field.type === FIELD_TYPES.object) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return object.$attributes[originKey]
    }
    if (field?.originRelationship) {
      const relationshipMapping = getRelationshipMapping(object.$type)
      const originRelation = relationshipMapping[field.originRelationship]
      if (!originRelation) {
        throw new Error(`Unknown relationKey '${originRelation}'`)
      }
      const relationshipName = originRelation.originKey
      const nested = object.$relationships?.[relationshipName]

      const target = nested ? nested.data : null

      if (!originRelation.isMultiple) {
        if (!target) {
          // eslint-disable-next-line consistent-return
          return
        }

        return target.id
      }
      if (target && target.length > 0) {
        const firstRow = target[0]

        return firstRow.id
      }

      return null
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return object.$attributes[originKey]
  }

  return object[key]
}

function getField(objectType, key) {
  if (storeDictionaryFields.has(objectType, key)) {
    return storeDictionaryFields.get(objectType, key)
  }
  const fields = getFields(objectType)
  if (fields === undefined) {
    throw new Error(`found no fields for objectType '${objectType}'`)
  }
  const field = fields.find((field) => field.key === key)

  storeDictionaryFields.set(objectType, key, field)

  return field
}

// TODO : need mutualise some parts of getter and setter
function setter(object, key, value) {
  const directKeys = {
    meta: true,
    links: true,
    id: true,
  }
  object.$version++ // increment to improve reactivity
  if (directKeys[key]) {
    object[key] = value

    return true
  }

  const firstLetter = key.slice(0, 1)

  if (firstLetter === '$') {
    object[key] = value

    return true
  }
  const relationshipMapping = getRelationshipMapping(object.$type)

  if (firstLetter === '_') {
    const id = typeof value === 'object' ? null : value

    const relation = relationshipMapping[key]

    if (!relation) {
      throw new Error(
        `Unknown relation '${key}' for '${object.$type}' objectType`,
      )
    }

    const { originKey, objectType } = relation
    const relationship = object.$relationships[originKey]
    const data = relationship ? relationship.data : null

    if (!data) {
      object.$relationships[originKey] = {
        data: id
          ? {
              id,
              type: objectType,
            }
          : value,
      }
      object.$hasChanged = true

      return true
    }
    if (id) {
      if (data.id !== id) {
        data.id = id
        object.$hasChanged = true
      }

      return true
    }
    if (!isEqual(relationship.data, value)) {
      relationship.data = value
      object.$hasChanged = true
    }

    return true
  }
  const mapping = getFieldMapping(object.$type)

  const originKey = mapping[key]
  if (!originKey) {
    throw new Error(`Unknown key '${key}' for '${object.$type}' objectType`)
  }

  const field = getField(object.$type, key)

  if (field?.originRelationship) {
    const originRelation = relationshipMapping[field.originRelationship]
    if (!originRelation) {
      throw new Error(`Unknown relationKey '${originRelation}'`)
    }
    const relationshipName = originRelation.originKey

    if (!Object.hasOwn(object.$relationships, relationshipName)) {
      object.$relationships[relationshipName] = {
        data: originRelation.isMultiple ? [] : null,
      }
    }
    const nested = object.$relationships[relationshipName]
    const target = nested.data
    const { targetKey } = field

    if (!originRelation.isMultiple) {
      if (!target) {
        if (targetKey === 'id') {
          nested.data = {
            type: originRelation.objectType,
            id: value,
          }
          object.$hasChanged = true
        } else {
          const recordNew = createNewRecord(originRelation.objectType)
          recordNew[targetKey] = value
          nested.data = recordNew
        }
      } else if (target[targetKey] !== value) {
        target[targetKey] = value
        object.$hasChanged = true
      }
    } else if (!target || target.length === 0) {
      nested.data = valueIsEmpty(value)
        ? null
        : [
            {
              type: originRelation.objectType,
              id: value,
            },
          ]
    } else {
      const firstRow = target[0]
      if (firstRow.$isNew) {
        firstRow[targetKey] = value
      } else {
        const targetObject = recordStore.get(firstRow.type, firstRow.id)
        if (targetObject) {
          targetObject[targetKey] = value
        }
      }
    }
  } else if (
    object.$attributes[originKey] !== value &&
    field.isPseudoAttribute
  ) {
    object.$attributes[originKey] = value
  } else if (object.$attributes[originKey] !== value) {
    if (!Object.hasOwn(object.$attributesOrigin, originKey)) {
      // store original value
      object.$attributesOrigin[originKey] = object.$attributes[originKey]

      if (field.attachedFieldTag) {
        const fields = getFields(object.$type)
        const similarAttachedFields = getSimilarAttachedFields(
          fields,
          field.attachedFieldTag,
        )
        for (const originKey of similarAttachedFields) {
          if (!Object.hasOwn(object.$attributesOrigin, originKey)) {
            object.$attributesOrigin[originKey] = object.$attributes[originKey]
          }
        }
      }
    } else if (object.$attributesOrigin[originKey] === value) {
      // back to previous value
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete object.$attributesOrigin[originKey]
    }
    object.$attributes[originKey] = value
    object.$hasChanged = Object.keys(object.$attributesOrigin).length > 0
  }
  refreshDependentFields(object, field)

  return true
}

function refreshDependentFields(
  object,
  attribute: FieldInterface | RelationInterface,
) {
  for (const dependentFieldName of attribute.dependentFieldKeys || []) {
    const dependantField = getField(object.$type, dependentFieldName)
    refreshField(object, dependantField)
  }
}

function refreshField(object, field) {
  if (field.expression) {
    const { originKey, expression } = field
    const copy = copyRecord(object, { doNotStore: true })
    object.$attributes[originKey] = evalExpr(expression, copy)
  }
}

function setRelationshipValue(object, key, value) {
  const mapping = getRelationshipMapping(object.$type)
  const relation = mapping[key]
  if (relation?.isMultiple && value) {
    addRelationship(object, key, value)
  } else if (value) {
    // TODO manage single
    throw new Error('Unmanaged case')
  } else {
    clearRelationship(object, key)
  }
}

function setRelationship(object, key, value) {
  const id = typeof value === 'object' ? value.id : value

  const mapping = getRelationshipMapping(object.$type)
  const relation = mapping[key]

  if (!relation) {
    throw new Error(
      `Unknown relation '${key}' for '${object.$type}' objectType`,
    )
  }

  if (relation.isMultiple) {
    throw new Error(
      `Relation '${key}' for '${object.$type}' objectType should not be multiple`,
    )
  }

  const { originKey, objectType } = relation
  const relationship = object.$relationships[originKey]
  const data = relationship ? relationship.data : null

  if (!data) {
    object.$relationships[originKey] = {
      data: {
        id,
        type: objectType,
      },
    }

    object.$relationshipsHasChanged = true

    return true
  }
  data.id = id
  object.$relationshipsHasChanged = true

  return true
}

function addRelationship(object, key, value, shouldChangeRelationship = true) {
  const id = typeof value === 'object' ? value.id : value

  const mapping = getRelationshipMapping(object.$type)
  const relation = mapping[key]

  if (!relation) {
    throw new Error(
      `Unknown relation '${key}' for '${object.$type}' objectType`,
    )
  }

  if (!relation.isMultiple) {
    throw new Error(
      `Relation '${key}' for '${object.$type}' objectType should be multiple`,
    )
  }

  const { originKey, objectType } = relation
  const relationship = object.$relationships[originKey]

  const data = relationship ? relationship.data : null

  const relationshipNew = {
    id,
    type: objectType,
  }

  if (data) {
    data.push(relationshipNew)
  } else {
    object.$relationships[originKey] = {
      data: [relationshipNew],
    }
  }
  object.$relationshipsHasChanged = shouldChangeRelationship

  if (!object.$relationshipsAdded[originKey]) {
    object.$relationshipsAdded[originKey] = []
  }
  object.$relationshipsAdded[originKey].push(relationshipNew)

  return true
}

function clearRelationship(object, key) {
  const mapping = getRelationshipMapping(object.$type)
  const relation = mapping[key]

  if (!relation) {
    throw new Error(
      `Unknown relation '${key}' for '${object.$type}' objectType`,
    )
  }

  if (!relation.isMultiple) {
    throw new Error(
      `Relation '${key}' for '${object.$type}' objectType should not be multiple`,
    )
  }

  const { originKey } = relation
  const relationship = object.$relationships[originKey]

  if (
    relationship === undefined ||
    (Array.isArray(relationship.data) && relationship.data.length === 0)
  ) {
    return
  }
  if (!Array.isArray(relationship.data)) {
    relationship.data = []
    return
  }
  for (const row of relationship.data) {
    delRelationship(object, key, row)
  }
}

function delRelationship(object, key, value) {
  const id = typeof value === 'object' ? value.id : value

  const mapping = getRelationshipMapping(object.$type)
  const relation = mapping[key]

  if (!relation) {
    throw new Error(
      `Unknown relation '${key}' for '${object.$type}' objectType`,
    )
  }

  if (!relation.isMultiple) {
    throw new Error(
      `Relation '${key}' for '${object.$type}' objectType should be multiple`,
    )
  }

  const { originKey } = relation
  const relationship = object.$relationships[originKey]
  const data = relationship ? relationship.data : null

  const index = data.findIndex((row) => row.id === id)
  if (index < 0) {
    throw new Error(`Unknown id '${id}'`)
  }

  const deleted = data.splice(index, 1)

  if (!object.$relationshipsDeleted[originKey]) {
    object.$relationshipsDeleted[originKey] = []
  }
  object.$relationshipsDeleted[originKey].push(deleted[0])
  object.$relationshipsHasChanged = true
}

export function deserialize<T>(data, links?): T {
  const o = {
    id: data.id,
    $type: data.type,
    $isProxyRecord: true,
    $attributes: data.attributes,
    $attributesOrigin: {},
    $meta: data.meta,
    $relationships: data.relationships,
    $relationshipsDeleted: {},
    $relationshipsAdded: {},
    $relationshipsById: {},
    $errors: {},
    $hasChanged: false,
    $relationshipsHasChanged: false,
    $hasErrors: false,
    $isNew: false,
    $uxContext: '',
    $version: 0,
    $controls: {},
  }
  if (links) {
    for (const link of links) {
      deserialize(link)
    }
  }
  const record = makeRecord(o)

  return record
}

function makeRecord<T = any>(o, doNotStore = false): T {
  o.$disabledAttributes = o.$meta?.disabled_attributes
    ? zipObject(o.$meta.disabled_attributes, true)
    : {}
  o.$setRelationship = (key, attributes) => setRelationship(o, key, attributes)
  o.$setRelationshipValue = (key, value) => setRelationshipValue(o, key, value)
  o.$clearRelationship = (key) => clearRelationship(o, key)
  o.$addRelationship = (key, attributes, shouldChangeRelationship) =>
    addRelationship(o, key, attributes, shouldChangeRelationship)
  o.$delRelationship = (key, id) => delRelationship(o, key, id)
  o.$setError = (key, message) => {
    o.$errors[key] = message
    o.$hasErrors = Object.keys(o.$errors).length > 0
  }
  o.$setErrors = (errors: Warning[]) => {
    const fieldsAndRelationsByOriginKey = getFieldsAndRelationsByOriginKey(
      o.$type,
    )
    const $errors = getFormattedErrors(fieldsAndRelationsByOriginKey, errors)
    const $hasErrors = Object.keys($errors).length > 0

    Object.assign(o, { $errors, $hasErrors })
  }
  o.$clearErrors = () => {
    o.$setErrors([])
  }
  o.$clearError = (key) => {
    if (Object.hasOwn(o.$errors, key)) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete o.$errors[key]
    }
  }
  o.$hasError = (key) => Object.hasOwn(o.$errors, key)

  o.$hasValue = (field) =>
    Object.hasOwn(o.$attributes, field.originKey) &&
    o.$attributes[field.originKey] !== null

  o.$getDiff = (availableFields?: string[]) => {
    const diffKeys = Object.keys(o.$attributesOrigin)
    const availableFieldsByKey = availableFields
      ? _.indexBy(availableFields, 'originKey')
      : {}

    const diff = diffKeys.reduce((diff, key) => {
      diff[key] = availableFieldsByKey[key] ? o.$attributes[key] : null

      return diff
    }, {})

    return diff
  }
  o.$restore = () => {
    for (const originKey of Object.keys(o.$attributesOrigin)) {
      o.$attributes[originKey] = o.$attributesOrigin[originKey]
    }
    o.$attributesOrigin = {}
    o.$hasChanged = false
    o.$relationshipsDeleted = {}
    o.$relationshipsAdded = {}
    o.$relationshipsHasChanged = false
    o.$setErrors()
  }

  o.$fieldHasChanged = (key) => {
    const mapping = getFieldMapping(o.$type)
    const originKey = mapping[key]
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return Object.hasOwn(o.$attributesOrigin, originKey!)
  }

  o.$getOriginValue = (originKey) =>
    Object.hasOwn(o.$attributesOrigin, originKey)
      ? o.$attributesOrigin[originKey]
      : o.$attributes[originKey]

  o.$setUxContext = ($uxContext) => {
    o.$uxContext = $uxContext
  }
  o.$refreshDependentFields = (field: FieldInterface): void =>
    refreshDependentFields(o, field)
  o.$refreshAllDependentFields = (): void => {
    const fields = getFields(o.$type) ?? []
    for (const field of fields) {
      refreshDependentFields(deserialized, field)
    }
    const relations = getRelations(o.$type) ?? []
    for (const relation of relations) {
      refreshDependentFields(deserialized, relation)
    }
  }

  o.$refreshField = (fieldKey: string) => {
    const field = getField(o.$type, fieldKey)
    refreshField(o, field)
  }

  o.$setMin = (fieldKey: string, min: number | string | Date | null) => {
    const fieldControl = getFieldControl(o, fieldKey)
    fieldControl.hasMin = true
    fieldControl.min = min
  }

  o.$setMax = (fieldKey: string, max: number | string | Date | null) => {
    const fieldControl = getFieldControl(o, fieldKey)
    fieldControl.hasMax = true
    fieldControl.max = max
  }

  o.$getHasErrors = () => getHasErrors(o)

  const deserialized = new Proxy(o, {
    get(object, key) {
      return getter(object, key)
    },
    set(object, key, value) {
      return setter(object, key, value)
    },
  })

  if (!doNotStore) {
    recordStore.set(deserialized.$type, deserialized.id, deserialized, true)
  }

  return deserialized
}

export function getFlatRecord(record) {
  const fields = getFields(record.$type)
  if (fields === undefined) {
    throw new Error(`found no fields for record type '${record.$type}'`)
  }
  return fields.reduce(
    (flatRecord, field) => {
      flatRecord[field.key] = record[field.key]

      return flatRecord
    },
    { id: record.id },
  )
}

export type CopyRecordOptions = {
  keepUxContext: boolean
  doNotStore: boolean
  giveUUID: boolean
}

export function copyRecord<T extends ProxyRecord = any>(
  record: ProxyRecord,
  options: Partial<CopyRecordOptions> = {},
): T {
  const { keepUxContext, doNotStore, giveUUID } = {
    keepUxContext: false,
    doNotStore: false,
    giveUUID: false,
    ...options,
  }

  const id = giveUUID ? getUUID() : record.id

  const o = {
    id,
    $type: record.$type,
    $isProxyRecord: true,
    $attributes: deepCopy(record.$attributes),
    $attributesOrigin: deepCopy(record.$attributesOrigin),
    $meta: deepCopy(record.$meta),
    $relationships: deepCopy(record.$relationships),
    $relationshipsDeleted: deepCopy(record.$relationshipsDeleted),
    $relationshipsAdded: deepCopy(record.$relationshipsAdded),
    $relationshipsById: deepCopy(record.$relationshipsById),
    $errors: deepCopy(record.$errors),
    $hasChanged: record.$hasChanged,
    $relationshipsHasChanged: record.$relationshipsHasChanged,
    $hasErrors: record.$hasErrors,
    $isNew: record.$isNew,
    $uxContext: keepUxContext ? record.$uxContext : '',
    $version: record.$version,
    $controls: deepCopy(record.$controls),
  }
  return makeRecord<T>(o, doNotStore)
}

function getTarget(
  $type,
  id,
  $uxContext = '',
  $attributes = {},
  $relationships = {},
) {
  return {
    id: id || random(TEMPO_ID),
    $type,
    $isProxyRecord: true,
    $attributes,
    $attributesOrigin: {},
    $meta: {},
    $relationships,
    $relationshipsDeleted: {},
    $relationshipsAdded: {},
    $relationshipsById: {},
    $errors: {},
    $hasChanged: false,
    $relationshipsHasChanged: false,
    $hasErrors: false,
    $isNew: !id,
    $uxContext,
    $version: 0,
    $controls: {},
  }
}

export function createNewRecord<Type extends ProxyRecord = any>(
  $type,
  defaultValue: Record<string, any> = {},
  relationships: Record<string, any> = {},
  $uxContext = '',
) {
  const o = getTarget($type, defaultValue.id, $uxContext)
  const record: ProxyRecord = makeRecord<Type>(o)
  const fields = getFields($type)

  setDefaultValues(record, fields, defaultValue)

  for (const key of Object.keys(relationships)) {
    record[key] = relationships[key]
  }

  return record as Type
}

export { getRecord, removeRecordFromCache } from './recordStore'

function getFieldControl(record: ProxyRecord, fieldKey: string): FieldControl {
  return (
    record.$controls[fieldKey] ?? {
      hasMin: false,
      hasMax: false,
      min: null,
      max: null,
    }
  )
}

/**
 * Returns true if min or max controls are not respected
 * TODO should be refact when using true object classes
 * @param record
 */
function getHasErrors(record: ProxyRecord): boolean {
  const fields = getFields(record.$type)

  const hasErrors = fields?.reduce((hasErrors, field) => {
    const fieldControl = getFieldControl(record, field.key)
    if (!hasErrors && fieldControl?.hasMin) {
      const value = record.$attributes[field.originKey] as
        | number
        | string
        | Date
        | null
      if (
        value === null ||
        (fieldControl.min !== null && value < fieldControl.min)
      ) {
        return true
      }
    }
    if (!hasErrors && fieldControl?.hasMax) {
      const value = record.$attributes[field.originKey] as
        | number
        | string
        | Date
        | null
      if (
        value !== null &&
        fieldControl.max !== null &&
        value > fieldControl.max
      ) {
        return true
      }
    }
    return hasErrors
  }, false)

  return hasErrors || false
}

export function getSimilarAttachedFields(
  fields: FieldInterface[],
  attachedFieldTag: string,
): string[] {
  return fields.reduce<string[]>(
    (attachedFields, { originKey, attachedFieldTag: _attachedFieldTag }) => {
      if (
        _attachedFieldTag === attachedFieldTag &&
        !attachedFields.includes(originKey)
      ) {
        attachedFields.push(originKey)
      }
      return attachedFields
    },
    [],
  )
}
